'use client';

import { type ComponentProps, type ReactElement } from 'react';
import { type BoxProps } from '@ironhack/design-system2/components/box';
import { Grid, GridItem } from '@ironhack/design-system2/components/grid';
import { Text, type TextProps } from '@ironhack/design-system2/components/text';
import { useBreakpointValue } from '@ironhack/design-system2/hooks';

import { ModuleWrapper } from '@/app/components/client/module-wrapper';
import { GridCard } from '@/app/components/server/grid-card';
import {
  type DatoGridCardBlock,
  type DatoModuleFormat,
} from '@/lib/datocms/types';

export type GridModule = {
  cards: DatoGridCardBlock[];
  headingProps?: TextProps;
  moduleFormat: [DatoModuleFormat];
  subtitle: string;
  title: string;
};

type Props = { data: GridModule } & BoxProps;

export const GridModule = (props: Props): ReactElement => {
  const {
    data: {
      cards,
      headingProps = {},
      moduleFormat: [moduleFormat],
      subtitle,
      title,
    },
    ...boxProps
  } = props;

  const cardOrientation = useBreakpointValue({
    base: 'vertical',
    sm: 'horizontal',
  });

  return (
    <ModuleWrapper
      innerProps={{ direction: 'column', gap: 3 }}
      moduleFormat={moduleFormat}
      outerProps={boxProps}
    >
      <Text as="h2" textStyle="3xl" {...headingProps}>
        {title}
      </Text>
      <Text textStyle="l">{subtitle}</Text>
      <Grid
        columnGap={2}
        rowGap={[2, null, 3]}
        templateColumns="repeat(auto-fill, 156px)"
      >
        {cards.map(({ id, image, ...card }) => (
          <GridItem
            key={id}
            {...(image && { colSpan: [1, 2], rowSpan: [2, 1] })}
          >
            <GridCard
              card={{ image, ...card }}
              {...(image && {
                orientation: cardOrientation as ComponentProps<
                  typeof GridCard
                >['orientation'],
              })}
            />
          </GridItem>
        ))}
      </Grid>
    </ModuleWrapper>
  );
};
